// src/lib/api.ts
// Central API client — all network calls go through here.
// This is where you'll add: auth headers, token refresh, encryption, logging, retries.

import { discardInvalidHvJwt, getValidHvJwt, isHvJwtRequiredPath } from '../utils/hvJwt';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';

export type ApiResponse<T> =
  | { ok: true; data: T; status: number }
  | { ok: false; error: string; status: number; permission?: string; acceptanceRequired?: boolean };

async function request<T>(
  path: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  const token = getValidHvJwt();
  if (!token) {
    discardInvalidHvJwt();
    if (isHvJwtRequiredPath(path)) {
      return { ok: false, error: 'Skipped: no valid hv_jwt', status: 0 };
    }
  }

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  try {
    const res = await fetch(`${BASE_URL}${path}`, { ...options, headers });

    // Handle non-JSON responses gracefully
    const contentType = res.headers.get('content-type') ?? '';
    const data = contentType.includes('application/json')
      ? await res.json()
      : await res.text();

    if (!res.ok) {
      return {
        ok: false,
        error: (data as any)?.error ?? (data as any)?.message ?? `HTTP ${res.status}`,
        status: res.status,
        permission: (data as any)?.permission,
        acceptanceRequired: Boolean((data as any)?.acceptanceRequired),
      };
    }

    return { ok: true, data: data as T, status: res.status };
  } catch (err) {
    // Network failure, CORS, timeout, etc.
    return {
      ok: false,
      error: err instanceof Error ? err.message : 'Network error',
      status: 0,
    };
  }
}

export const api = {
  get: <T>(path: string, options?: RequestInit) =>
    request<T>(path, { ...options, method: 'GET' }),

  post: <T>(path: string, body: unknown, options?: RequestInit) =>
    request<T>(path, {
      ...options,
      method: 'POST',
      body: JSON.stringify(body),
    }),

  put: <T>(path: string, body: unknown, options?: RequestInit) =>
    request<T>(path, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  patch: <T>(path: string, body: unknown, options?: RequestInit) =>
    request<T>(path, {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  delete: <T>(path: string, options?: RequestInit) =>
    request<T>(path, { ...options, method: 'DELETE' }),
};

// Usage example:
// const result = await api.get<User[]>('/users');
// if (result.ok) { use result.data } else { show result.error }